/**
 * useStandaloneMode — React hook that returns `true` when the app is running
 * as an installed PWA (home-screen / app drawer launch), `false` when it's
 * running in a normal browser tab.
 *
 * Detection:
 *  • `@media (display-mode: standalone)` — supported by Chrome, Edge, Samsung
 *    Internet, Safari 16.4+ and PWABuilder-wrapped TWAs / iOS shells.
 *  • `window.navigator.standalone === true` — iOS Safari's legacy home-screen
 *    check (still needed for iOS < 16.4).
 *  • `?standalone=1` URL param — dev/preview override so we can eyeball the
 *    native-feel UI from a normal browser without actually installing.
 *
 * Also toggles a `standalone-mode` class on <html> so CSS can gate styles
 * without having to plumb the prop into every component.
 */

import { useEffect, useState } from "react";

function detect() {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("standalone") === "1") return true;
  } catch {
    // ignore malformed URLs — detection falls through to the media queries
  }
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

export function useStandaloneMode() {
  const [isStandalone, setIsStandalone] = useState(detect);

  useEffect(() => {
    // Keep <html>.standalone-mode in sync so CSS selectors can gate styles.
    document.documentElement.classList.toggle("standalone-mode", isStandalone);
  }, [isStandalone]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(display-mode: standalone)");
    const handler = () => setIsStandalone(detect());
    // Older Safari only supports addListener/removeListener on MediaQueryList.
    if (mq.addEventListener) {
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return isStandalone;
}
